/**
 * Module dependencies.
 */

import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from 'react';

import { useFiltersList } from 'src/context/filters-list/context';
import { useRouter } from 'next/router';
import BaseSidebar from 'src/components/filters/base/base-sidebar';
import qs from 'qs';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  isOpen: boolean;
  isUpdating: boolean;
  onClose: () => void;
  onUpdate: () => void;
};

/**
 * `BaseFiltersSidebar` component.
 */

const BaseFiltersSidebar = (props: Props): ReactElement => {
  const { children, className, isOpen, isUpdating, onClose, onUpdate } = props;
  const { omitUrl, onSubmit, onSubmitClear, reset } = useFiltersList();
  const [forceUpdate, setForceUpdate] = useState<boolean>();
  const { translate } = useTranslate();
  const router = useRouter();
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleUpdate = useCallback(
    filters => {
      if (omitUrl) {
        onClose();
        setForceUpdate(true);

        return;
      }

      router
        .push(
          {
            pathname: router.pathname,
            query: qs.stringify({ filter: filters })
          },
          undefined,
          { shallow: true }
        )
        .then(() => {
          onUpdate();
          onClose();
        });
    },
    [omitUrl, onClose, onUpdate, router]
  );

  const handleClear = useCallback(() => {
    const { submittedFilters } = onSubmitClear();

    handleUpdate(submittedFilters);
  }, [handleUpdate, onSubmitClear]);

  const handleSubmit = useCallback(() => {
    const { submittedFilters } = onSubmit();

    handleUpdate(submittedFilters);
  }, [handleUpdate, onSubmit]);

  useEffect(() => {
    if (omitUrl && forceUpdate) {
      const timeout = setTimeout(() => {
        onUpdate();
        setForceUpdate(false);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [forceUpdate, omitUrl, onUpdate]);

  return (
    <BaseSidebar
      className={className}
      isOpen={isOpen}
      isUpdating={isUpdating}
      onClear={handleClear}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={translate('common:labels.filters')}
    >
      {children}
    </BaseSidebar>
  );
};

/**
 * Export `BaseFiltersSidebar` component.
 */

export default BaseFiltersSidebar;
